import { useState, useEffect } from 'react';
import { History, PackageOpen, ChevronDown, ChevronUp, Calendar, Package, Tag, Euro, FileSpreadsheet, Database, Download, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';

// Format price helper
const formatPrice = (value) => {
    if (value === undefined || value === null) return '0,00';
    return value.toFixed(2).replace('.', ',');
};

// Format date helper
const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

export const IstoricPaleti = () => {
    const [loading, setLoading] = useState(true);
    const [pallets, setPallets] = useState([]);
    const [expandedPallet, setExpandedPallet] = useState(null);
    const [palletItems, setPalletItems] = useState({});
    const [allowDelete, setAllowDelete] = useState(false);
    const [exportingAll, setExportingAll] = useState(false);

    useEffect(() => {
        loadPallets();
        loadSettings();
    }, []);

    const loadPallets = async () => {
        setLoading(true);
        try {
            const response = await api.get('/pallets/history');
            setPallets(response.data || []);
        } catch (err) {
            console.error('Error loading pallets:', err);
        }
        setLoading(false);
    };
    
    const loadSettings = async () => {
        try {
            const response = await api.get('/settings/v2');
            if (response.data) {
                setAllowDelete(response.data.allow_delete_pallets || false);
            }
        } catch (err) {
            console.error('Error loading settings:', err);
        }
    };

    const exportAllPallets = async () => {
        setExportingAll(true);
        try {
            const response = await api.get('/pallets/export/excel/all', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Toate_Paletele_${new Date().toLocaleDateString('ro-RO').replace(/\./g, '-')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Export complet descărcat!');
        } catch (err) {
            console.error('Export error:', err);
            toast.error(err.response?.data?.detail || 'Eroare la export');
        }
        setExportingAll(false);
    };
    
    const handleDeletePallet = async (pallet) => {
        if (!window.confirm(`Sigur vrei să ștergi Paletul #${pallet.number}?\n\nAceastă acțiune este ireversibilă și va șterge toate produsele asociate.`)) {
            return;
        }
        
        try {
            await api.delete(`/pallets/${pallet.id}/history`);
            toast.success(`Paletul #${pallet.number} a fost șters`);
            loadPallets();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la ștergere');
        }
    };

    const toggleExpand = async (palletId) => {
        if (expandedPallet === palletId) {
            setExpandedPallet(null);
            return;
        }
        
        setExpandedPallet(palletId);
        
        // Load items if not already loaded
        if (!palletItems[palletId]) {
            try {
                const response = await api.get(`/pallets/${palletId}/items`);
                setPalletItems(prev => ({ ...prev, [palletId]: response.data || [] }));
            } catch (err) {
                console.error('Error loading items:', err);
            }
        }
    };

    // Group items by category
    const groupByCategory = (items) => {
        const grouped = {};
        for (const item of items) {
            const cat = item.category || 'Necategorizat';
            if (!grouped[cat]) {
                grouped[cat] = [];
            }
            grouped[cat].push(item);
        }
        return grouped;
    };

    if (loading) {
        return (
            <div className="pb-24">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Istoric Paleți
                    </h1>
                    <p className="text-zinc-500 mt-1">Toate paletele recepționate</p>
                </div>
                <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="bg-white border border-zinc-200 rounded-lg p-4">
                            <Skeleton className="h-6 w-32 mb-2" />
                            <Skeleton className="h-4 w-48" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="pb-24">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Istoric Paleți
                    </h1>
                    <p className="text-zinc-500 mt-1">
                        {pallets.length} paleți recepționați
                    </p>
                </div>
                <Button
                    onClick={exportAllPallets}
                    disabled={exportingAll}
                    className="gap-2 bg-emerald-500 hover:bg-emerald-600"
                    data-testid="export-all-pallets-btn"
                >
                    <FileSpreadsheet size={18} className={exportingAll ? 'animate-pulse' : ''} />
                    {exportingAll ? 'Export...' : 'Export Toți Paleții'}
                </Button>
            </div>

            {/* Pallets List */}
            {pallets.length === 0 ? (
                <div className="bg-zinc-100 border border-zinc-200 rounded-xl p-8 text-center">
                    <PackageOpen size={48} className="mx-auto text-zinc-400 mb-3" />
                    <h2 className="font-bold text-zinc-700 mb-2">Niciun palet</h2>
                    <p className="text-zinc-500">Nu există paleți în istoric</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {pallets.map((pallet) => {
                        const isExpanded = expandedPallet === pallet.id;
                        const items = palletItems[pallet.id] || [];
                        const totalItems = pallet.total_items || items.reduce((sum, i) => sum + i.quantity, 0);
                        const estimatedPrice = totalItems > 0 ? (pallet.purchase_value || 0) / totalItems : 0;
                        const groupedItems = groupByCategory(items);
                        
                        return (
                            <div
                                key={pallet.id}
                                className={`bg-white border rounded-xl overflow-hidden transition-all ${
                                    pallet.status === 'open' 
                                        ? 'border-amber-300 bg-amber-50/30' 
                                        : 'border-zinc-200'
                                }`}
                            >
                                {/* Pallet Header */}
                                <button
                                    onClick={() => toggleExpand(pallet.id)}
                                    className="w-full p-4 flex items-center justify-between hover:bg-zinc-50 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${
                                            pallet.status === 'open' ? 'bg-amber-100' : 'bg-zinc-100'
                                        }`}>
                                            <PackageOpen size={24} className={
                                                pallet.status === 'open' ? 'text-amber-600' : 'text-zinc-500'
                                            } />
                                        </div>
                                        <div className="text-left">
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                    Palet #{pallet.number}
                                                </h3>
                                                {pallet.status === 'open' && (
                                                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                                        Activ
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3 text-sm text-zinc-500 mt-1">
                                                <span className="flex items-center gap-1">
                                                    <Calendar size={14} />
                                                    {formatDate(pallet.opened_at)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-4">
                                        <div className="text-right">
                                            <p className="text-lg font-bold text-red-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                {formatPrice(pallet.purchase_value || 0)} RON
                                            </p>
                                            <p className="text-sm text-zinc-500">
                                                {totalItems} buc
                                            </p>
                                        </div>
                                        {isExpanded ? (
                                            <ChevronUp size={20} className="text-zinc-400" />
                                        ) : (
                                            <ChevronDown size={20} className="text-zinc-400" />
                                        )}
                                    </div>
                                </button>
                                
                                {/* Expanded Details */}
                                {isExpanded && (
                                    <div className="border-t border-zinc-200 p-4 bg-zinc-50/50">
                                        {/* Export Buttons */}
                                        <div className="flex gap-2 mb-4">
                                            <Button
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    try {
                                                        toast.info('Se pregătește exportul Excel...');
                                                        const response = await api.get(
                                                            `/pallets/${pallet.id}/export/excel`,
                                                            { responseType: 'blob' }
                                                        );
                                                        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                                                        const url = window.URL.createObjectURL(blob);
                                                        const a = document.createElement('a');
                                                        a.href = url;
                                                        a.download = `palet_${pallet.number}.xlsx`;
                                                        document.body.appendChild(a);
                                                        a.click();
                                                        window.URL.revokeObjectURL(url);
                                                        a.remove();
                                                        toast.success('Export Excel descărcat!');
                                                    } catch (err) {
                                                        console.error(err);
                                                        toast.error('Eroare la export Excel');
                                                    }
                                                }}
                                                variant="outline"
                                                size="sm"
                                                className="flex-1 border-green-300 text-green-700 hover:bg-green-50"
                                                data-testid="export-excel-btn"
                                            >
                                                <FileSpreadsheet size={16} className="mr-2" />
                                                Export Excel
                                            </Button>
                                            <Button
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    try {
                                                        toast.info('Se pregătește exportul DBF...');
                                                        const response = await fetch(
                                                            `${process.env.REACT_APP_BACKEND_URL}/api/pallets/${pallet.id}/export/dbf`,
                                                            { headers: { 'X-App-Version': localStorage.getItem('app_version') || 'v2' } }
                                                        );
                                                        if (!response.ok) throw new Error('Export failed');
                                                        const blob = await response.blob();
                                                        const url = window.URL.createObjectURL(blob);
                                                        const a = document.createElement('a');
                                                        a.href = url;
                                                        a.download = `palet_${pallet.number}.dbf`;
                                                        document.body.appendChild(a);
                                                        a.click();
                                                        window.URL.revokeObjectURL(url);
                                                        a.remove();
                                                        toast.success('Export DBF descărcat!');
                                                    } catch (err) {
                                                        console.error(err);
                                                        toast.error('Eroare la export DBF');
                                                    }
                                                }}
                                                variant="outline"
                                                size="sm"
                                                className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                                                data-testid="export-dbf-btn"
                                            >
                                                <Database size={16} className="mr-2" />
                                                Export DBF
                                            </Button>
                                            
                                            {/* Delete Button - only visible if setting is enabled */}
                                            {allowDelete && pallet.status !== 'open' && (
                                                <Button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        handleDeletePallet(pallet);
                                                    }}
                                                    variant="outline"
                                                    size="sm"
                                                    className="border-red-300 text-red-600 hover:bg-red-50"
                                                    data-testid="delete-pallet-btn"
                                                >
                                                    <Trash2 size={16} className="mr-2" />
                                                    Șterge
                                                </Button>
                                            )}
                                        </div>
                                        
                                        {/* Summary */}
                                        <div className="grid grid-cols-3 gap-3 mb-4">
                                            <div className="bg-white p-3 rounded-lg border border-zinc-200 text-center">
                                                <p className="text-xs text-zinc-500">Val. Achiziție</p>
                                                <p className="text-lg font-bold text-red-600">
                                                    {formatPrice(pallet.purchase_value || 0)}
                                                </p>
                                            </div>
                                            <div className="bg-white p-3 rounded-lg border border-zinc-200 text-center">
                                                <p className="text-xs text-zinc-500">Total Bucăți</p>
                                                <p className="text-lg font-bold text-zinc-900">
                                                    {totalItems}
                                                </p>
                                            </div>
                                            <div className="bg-white p-3 rounded-lg border border-zinc-200 text-center">
                                                <p className="text-xs text-zinc-500">Preț/Buc</p>
                                                <p className="text-lg font-bold text-blue-600">
                                                    {formatPrice(estimatedPrice)}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        {/* Invoice Details */}
                                        {(pallet.furnizor || pallet.nr_factura || pallet.data_factura) && (
                                            <div className="bg-white p-3 rounded-lg border border-zinc-200 mb-4">
                                                <p className="text-xs text-zinc-500 mb-2 font-medium">Date Factură</p>
                                                <div className="grid grid-cols-2 gap-2 text-sm">
                                                    {pallet.furnizor && (
                                                        <div>
                                                            <span className="text-zinc-500">Furnizor:</span>{' '}
                                                            <span className="font-medium text-zinc-800">{pallet.furnizor}</span>
                                                        </div>
                                                    )}
                                                    {pallet.nr_factura && (
                                                        <div>
                                                            <span className="text-zinc-500">Nr. Factură:</span>{' '}
                                                            <span className="font-medium text-zinc-800">{pallet.nr_factura}</span>
                                                        </div>
                                                    )}
                                                    {pallet.data_factura && (
                                                        <div>
                                                            <span className="text-zinc-500">Data:</span>{' '}
                                                            <span className="font-medium text-zinc-800">{pallet.data_factura}</span>
                                                        </div>
                                                    )}
                                                    {pallet.scadenta && (
                                                        <div>
                                                            <span className="text-zinc-500">Scadență:</span>{' '}
                                                            <span className="font-medium text-zinc-800">{pallet.scadenta}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Items by Category */}
                                        {items.length === 0 ? (
                                            <p className="text-zinc-400 text-center py-4">Se încarcă...</p>
                                        ) : (
                                            <div className="space-y-4">
                                                {Object.entries(groupedItems).map(([category, categoryItems]) => (
                                                    <div key={category}>
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Tag size={14} className="text-violet-500" />
                                                            <span className="font-semibold text-zinc-700">{category}</span>
                                                            <span className="text-xs text-zinc-400">
                                                                ({categoryItems.reduce((s, i) => s + i.quantity, 0)} buc)
                                                            </span>
                                                        </div>
                                                        <div className="space-y-1 pl-5">
                                                            {categoryItems.map((item) => (
                                                                <div
                                                                    key={item.id}
                                                                    className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-zinc-100"
                                                                >
                                                                    <div>
                                                                        <p className="font-medium text-zinc-900">{item.name}</p>
                                                                        <p className="text-xs text-zinc-400 font-mono">{item.barcode}</p>
                                                                    </div>
                                                                    <div className="text-right">
                                                                        <p className="font-bold text-zinc-700">{item.quantity} buc</p>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default IstoricPaleti;
